import { View } from "@/components/ui";
import { UserCircle, EmailIcon, Phone } from "@/components/shared/icons";
import { useAuthState } from "@/store/auth";
import { useClientContactsQuery } from "@/gqlhooks/profile/useClientContactsQuery";
import { useClientAddressesQuery } from "@/gqlhooks/profile/useClientAddressesQuery";
import { fullName } from "@/lib/helpers/string";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/shared/link";
import { Skeleton } from "@/components/ui/skeleton";
import { ChangeNumberAction } from "../change-number-action";
import { Typography } from "../typography";
import { AccountDeletionCard } from "./account-deletion-card";
import { joinAddressParts } from "@/containers/profile/utils";

export const AccountInfoList = () => {
  const userInfo = useAuthState((state) => state.data.userInfo);
  const { data: contacts, loading: contactsLoading } = useClientContactsQuery();
  const { data: addresses, loading: addressesLoading } =
    useClientAddressesQuery();

  const account = userInfo?.user;
  const primaryContact = contacts?.find((i) => i.primary === true);
  const primaryAddress = addresses?.find((i) => i.primary === true);

  const [primaryEmail] = primaryContact?.email ?? [""];
  const [primaryPhoneNumber] = primaryContact?.phoneNumber ?? [""];
  const primaryAddressLabel = primaryAddress?.label;
  const primaryPostalCode = primaryAddress?.postalCode;
  const primaryFullAddress = primaryAddress?.fullAddress;
  const primaryUnitNumber = primaryAddress?.unitNumber;

  const hasLabel = Boolean(primaryAddressLabel?.toString().trim());
  const headerText = hasLabel ? primaryAddressLabel : primaryPostalCode;
  const secondLineText = hasLabel
    ? joinAddressParts(primaryPostalCode, primaryFullAddress, primaryUnitNumber)
    : joinAddressParts(primaryFullAddress, primaryUnitNumber);

  const isLoading = contactsLoading || addressesLoading;

  if (isLoading) {
    return (
      <View>
        <View className="mb-2 flex flex-col gap-2">
          <View className="mb-2 px-0 md:px-4">
            <Skeleton className="h-5 w-20" />
          </View>

          <View className="rounded-lg border border-border bg-card p-4">
            <View className="flex flex-row items-center gap-2 py-4 pr-0">
              <Skeleton className="size-7 rounded-full" />
              <View className="flex flex-1 flex-row items-center justify-between pr-4">
                <View>
                  <Skeleton className="mb-1 h-4 w-24" />
                  <Skeleton className="h-5 w-16" />
                </View>
                <Skeleton className="h-8 w-20 rounded" />
              </View>
            </View>

            <View className="mx-4 flex flex-row items-center gap-2 border-y border-secondary-border px-0 py-4">
              <Skeleton className="size-6 rounded" />
              <View>
                <Skeleton className="mb-1 h-4 w-12" />
                <Skeleton className="h-5 w-32" />
              </View>
            </View>

            <View className="flex flex-row items-center gap-2 py-4 pr-0">
              <Skeleton className="size-6 rounded" />
              <View className="flex flex-1 flex-row items-center justify-between pr-4">
                <View>
                  <Skeleton className="mb-1 h-4 w-24" />
                  <Skeleton className="h-5 w-28" />
                </View>
                <Skeleton className="h-8 w-24 rounded" />
              </View>
            </View>
          </View>
        </View>

        <View className="mb-2 flex flex-col gap-2">
          <View className="flex flex-1 flex-row justify-between py-2">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-5 w-16" />
          </View>

          <View className="rounded-lg border border-border bg-card p-4">
            <View className="flex flex-col gap-2 py-4">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-48" />
            </View>
          </View>
        </View>

        <View className="mb-8 flex flex-col gap-2">
          <View className="flex flex-1 flex-row justify-between py-2">
            <Skeleton className="h-5 w-28" />
            <Skeleton className="h-5 w-16" />
          </View>

          <View className="rounded-lg border border-border bg-card p-4">
            <View className="flex flex-col gap-2 py-4">
              <Skeleton className="h-5 w-20" />
              <Skeleton className="h-4 w-56" />
            </View>
          </View>
        </View>
      </View>
    );
  }

  return (
    <View>
      <View className="mb-2 flex flex-col gap-2">
        <View className="mb-2 px-0 md:px-4">
          <Typography variant="body-md">Account</Typography>
        </View>

        <Card>
          <CardContent className="flex flex-row items-center gap-2 py-4 pr-0">
            <UserCircle className="size-7 text-secondary-text" />
            <View className="flex flex-1 flex-row items-center justify-between pr-4">
              <View>
                <Typography variant="body-md" color="secondary-text">
                  Account Type
                </Typography>
                <Typography variant="body-md">
                  {userInfo?.accountType}
                </Typography>
              </View>
            </View>
          </CardContent>

          <CardContent className="mx-4 flex flex-row items-center gap-2 border-y border-secondary-border px-0 py-4">
            <EmailIcon className="size-6 text-secondary-text" />
            <View>
              <Typography variant="body-md" color="secondary-text">
                Email
              </Typography>
              <Typography variant="body-md">{account?.email}</Typography>
            </View>
          </CardContent>

          <CardContent className="flex flex-row items-center gap-2 py-4 pr-0">
            <Phone className="size-6 text-secondary-text" />
            <View className="flex flex-1 flex-row items-center justify-between pr-4">
              <View>
                <Typography variant="body-md" color="secondary-text">
                  Phone Number
                </Typography>
                <Typography variant="body-md">
                  {account?.phoneNumber}
                </Typography>
              </View>
              <ChangeNumberAction />
            </View>
          </CardContent>
        </Card>
      </View>

      <View className="mb-2 flex flex-col gap-2">
        <View className="flex flex-1 flex-row justify-between py-2">
          <Typography variant="body-md">Default Contact</Typography>
          <Link href="/profile/contacts">
            <Typography variant="body-md" color="brand-primary">
              View All
            </Typography>
          </Link>
        </View>

        <Card>
          <CardContent className="flex flex-col gap-2 py-4">
            <Typography variant="body-md">
              {fullName({
                firstName: userInfo?.firstName,
                lastName: userInfo?.lastName,
              })}
            </Typography>
            <Typography
              variant="body-md"
              color="secondary-text"
            >{`${primaryEmail}・ ${primaryPhoneNumber}`}</Typography>
          </CardContent>
        </Card>
      </View>

      <View className="mb-8 flex flex-col gap-2">
        <View className="flex flex-1 flex-row justify-between py-2">
          <Typography variant="body-md">Default Address</Typography>
          <Link href="/profile/addresses">
            <Typography variant="body-md" color="brand-primary">
              View All
            </Typography>
          </Link>
        </View>

        <Card>
          <CardContent className="flex flex-col gap-2 py-4">
            {headerText && (
              <Typography variant="body-md" numberOfLines={1}>
                {headerText}
              </Typography>
            )}

            {secondLineText && (
              <Typography variant="body-md" color="secondary-text">
                {secondLineText}
              </Typography>
            )}
          </CardContent>
        </Card>
      </View>

      <View>
        <AccountDeletionCard />
      </View>
    </View>
  );
};
